import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/core/stores/authStore';

// Define the possible user roles (matching backend UserRole enum)
export type UserRole =
  | 'family'
  | 'attorney'
  | 'vendor'
  | 'admin';

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: UserRole[];
  fallbackPath?: string;
}

/**
 * RoleGuard - Protects routes based on user roles
 *
 * Redirects users to their appropriate portal if they don't have
 * the required role for the current route.
 */
export function RoleGuard({
  children,
  allowedRoles,
  fallbackPath
}: RoleGuardProps) {
  const { user } = useAuthStore();

  // If no user, AuthGuard should have already redirected
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user's role is in the allowed roles
  const userRole = user.role;
  const hasRequiredRole = allowedRoles.includes(userRole as UserRole);

  if (!hasRequiredRole) {
    // Redirect to fallback or user's primary portal
    const redirectPath = fallbackPath || getDefaultPortalPath(userRole);
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}

/**
 * Get the default portal path based on user's role
 * Priority: admin > attorney > vendor > family
 */
export function getDefaultPortalPath(role: string): string {
  switch (role) {
    case 'admin':
      return '/admin/dashboard';
    case 'attorney':
      return '/attorney/dashboard';
    case 'vendor':
      return '/vendor/dashboard';
    case 'family':
    default:
      return '/family/dashboard';
  }
}

/**
 * Get all available portals for a user based on their role
 */
export function getAvailablePortals(role: string): Array<{
  name: string;
  path: string;
  icon: string;
  role: UserRole;
}> {
  const portals: Array<{
    name: string;
    path: string;
    icon: string;
    role: UserRole;
  }> = [];

  // Family users get family portal
  if (role === 'family') {
    portals.push({
      name: 'Family',
      path: '/family/dashboard',
      icon: 'Users',
      role: 'family',
    });
  }

  // Attorney users get attorney portal
  if (role === 'attorney') {
    portals.push({
      name: 'Attorney',
      path: '/attorney/dashboard',
      icon: 'Briefcase',
      role: 'attorney',
    });
  }

  // Vendor users get vendor portal
  if (role === 'vendor') {
    portals.push({
      name: 'Vendor',
      path: '/vendor/dashboard',
      icon: 'Store',
      role: 'vendor',
    });
  }

  // Admin users get all portals
  if (role === 'admin') {
    portals.push(
      {
        name: 'Admin',
        path: '/admin/dashboard',
        icon: 'Shield',
        role: 'admin',
      },
      {
        name: 'Family',
        path: '/family/dashboard',
        icon: 'Users',
        role: 'family',
      },
      {
        name: 'Attorney',
        path: '/attorney/dashboard',
        icon: 'Briefcase',
        role: 'attorney',
      },
      {
        name: 'Vendor',
        path: '/vendor/dashboard',
        icon: 'Store',
        role: 'vendor',
      }
    );
  }

  return portals;
}

export default RoleGuard;
